import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import type { NextRequest } from 'next/server';
import { verify } from 'jsonwebtoken';
import { verifyAccessTokenLocally } from '@west-tennessee-consulting/supabase';

// Environment configuration
const isDev = process.env.SUPABASE_ENV === 'dev' || process.env.NODE_ENV === 'development';

/**
 * Supabase clients for this module, built ON DEMAND and memoized.
 *
 * They used to be module-scope consts (`export const supabaseAdmin =
 * createClient(url, SERVICE_ROLE_KEY)`) — the pattern
 * BUILD_ENV_AND_EAGER_CLIENTS forbids — and they were reachable from the
 * `@west-tennessee-consulting/ui` BARREL: `index.ts` re-exports `./lib/calendar-creation-service`,
 * which imports `./auth-utils`, which imports this file. 711 files in apps/hub
 * import that barrel, so every one of them built a service-role client and a
 * GoTrueClient at import time.
 *
 * Three consequences, all real:
 *  - a service-role credential path was evaluated from a client-facing barrel
 *    (`index.ts` already keeps `./lib/auth-utils` OUT of the barrel for the
 *    related `next/server` reason; the calendar re-export pulled it back in);
 *  - `next build` evaluates server modules during "Collecting page data", so
 *    the factory ran with the BUILD env — the 2026-08-21 outage's exact shape;
 *  - each jest worker inherited live GoTrueClient timers, so every hub run
 *    ended in "Jest did not exit one second after the test run has completed",
 *    and a single-suite run could sit for minutes after a 4.6s test.
 *
 * Nothing outside this file read `supabaseAuth`/`supabaseAdmin` — the barrel
 * exports only the functions below — so this is not a public API change.
 */
function readEnv(devKey: string, prodKey: string, fallbackKey: string): string | undefined {
  return isDev
    ? process.env[devKey] || process.env[fallbackKey]
    : process.env[prodKey] || process.env[fallbackKey];
}

function supabaseUrl(): string | undefined {
  return readEnv(
    'NEXT_PUBLIC_SUPABASE_URL_DEV',
    'NEXT_PUBLIC_SUPABASE_URL_PROD',
    'NEXT_PUBLIC_SUPABASE_URL',
  );
}

// `undefined` = not built yet; `null` = built and deliberately unavailable
// (credentials missing). Distinguishing the two keeps the miss path from
// calling `createClient` again on every subsequent call.
let authClient: SupabaseClient | null | undefined;
let adminClient: SupabaseClient | null | undefined;

/** Anon-key client for end-user auth. `null` when the env is not configured. */
export function getSupabaseAuth(): SupabaseClient | null {
  if (authClient !== undefined) return authClient;
  const url = supabaseUrl();
  const anonKey = readEnv(
    'NEXT_PUBLIC_SUPABASE_ANON_KEY_DEV',
    'NEXT_PUBLIC_SUPABASE_ANON_KEY_PROD',
    'NEXT_PUBLIC_SUPABASE_ANON_KEY',
  );
  authClient = url && anonKey ? createClient(url, anonKey) : null;
  return authClient;
}

/**
 * Service-role client. Server-only: never call this from a component, or from
 * any module that can reach a client bundle.
 */
export function getSupabaseAdmin(): SupabaseClient | null {
  if (adminClient !== undefined) return adminClient;
  const url = supabaseUrl();
  const serviceKey = readEnv(
    'SUPABASE_SERVICE_ROLE_KEY_DEV',
    'SUPABASE_SERVICE_ROLE_KEY_PROD',
    'SUPABASE_SERVICE_ROLE_KEY',
  );
  if (!url || !serviceKey) {
    console.warn(
      '[Supabase Auth] Admin client not initialized - missing SUPABASE_SERVICE_ROLE_KEY or URL',
    );
    adminClient = null;
    return adminClient;
  }
  adminClient = createClient(url, serviceKey);
  return adminClient;
}

// ============================================================================
// SUPABASE JWT VERIFICATION UTILITIES
// ============================================================================

/**
 * Interface for Supabase JWT payload
 */
interface SupabaseJWTPayload {
  sub: string;
  email: string;
  role: string;
  aud: string;
  exp: number;
  iat: number;
  iss: string;
  user_metadata?: {
    userType?: string;
    [key: string]: any;
  };
  app_metadata?: {
    provider?: string;
    providers?: string[];
    [key: string]: any;
  };
}

/**
 * Verify Supabase JWT token and extract user information
 */
export function verifySupabaseJWT(token: string): SupabaseJWTPayload | null {
  try {
    // For Supabase, we should use the service role key as the JWT secret
    // or use Supabase's built-in verification
    const jwtSecret = process.env.SUPABASE_SERVICE_ROLE_KEY || 
                     process.env.SUPABASE_JWT_SECRET || 
                     process.env.NEXTAUTH_SECRET;
    
    if (!jwtSecret) {
      console.error('JWT secret not configured');
      console.error('Available env vars:', {
        SUPABASE_SERVICE_ROLE_KEY: !!process.env.SUPABASE_SERVICE_ROLE_KEY,
        SUPABASE_JWT_SECRET: !!process.env.SUPABASE_JWT_SECRET,
        NEXTAUTH_SECRET: !!process.env.NEXTAUTH_SECRET
      });
      
return null;
    }

    // Verify and decode the JWT token
    const decoded = verify(token, jwtSecret) as SupabaseJWTPayload;
    
    // Validate the token structure
    if (!decoded.sub || !decoded.email) {
      console.error('Invalid JWT payload structure:', {
        hasSub: !!decoded.sub,
        hasEmail: !!decoded.email,
        payload: decoded
      });
      
return null;
    }

    return decoded;
  } catch (error) {
    console.error('Error verifying Supabase JWT:', error);
    console.error('Token (first 50 chars):', token.substring(0, 50));
    
return null;
  }
}

/**
 * Extract user email from Supabase JWT token in authorization header
 */
export async function extractUserEmailFromSupabaseToken(request: NextRequest): Promise<string | null> {
  try {
    const authHeader = request.headers.get('authorization');
    if (!authHeader || !authHeader.startsWith('Bearer ')) {
      return null;
    }

    const token = authHeader.substring(7); // Remove 'Bearer ' prefix

    // Fast path: verify locally against the project JWKS (no GoTrue round-trip).
    // See the SUPABASE_JWT_LOCAL_VERIFICATION rule.
    const local = await verifyAccessTokenLocally(token);
    if (local.status === 'verified') return local.claims.email;
    if (local.status === 'expired') return null;

    const supabaseAdmin = getSupabaseAdmin();
    if (!supabaseAdmin) {
      console.warn('[Supabase Auth] extractUserEmailFromSupabaseToken: supabaseAdmin not initialized');
      return null;
    }
    // Network fallback (symmetric/legacy tokens or local-verify miss).
    const { data: { user }, error } = await supabaseAdmin.auth.getUser(token);

    if (error || !user) {
      console.error('Error verifying token with Supabase:', error);

return null;
    }

    return user.email || null;
  } catch (error) {
    console.error('Error extracting user email from Supabase token:', error);
    
return null;
  }
}

/**
 * Extract user ID from Supabase JWT token in authorization header
 */
export async function extractUserIdFromSupabaseToken(request: NextRequest): Promise<string | null> {
  try {
    const authHeader = request.headers.get('authorization');
    if (!authHeader || !authHeader.startsWith('Bearer ')) {
      return null;
    }

    const token = authHeader.substring(7); // Remove 'Bearer ' prefix

    // Fast path: verify locally against the project JWKS (no GoTrue round-trip).
    // See the SUPABASE_JWT_LOCAL_VERIFICATION rule.
    const local = await verifyAccessTokenLocally(token);
    if (local.status === 'verified') return local.claims.sub;
    if (local.status === 'expired') return null;

    const supabaseAdmin = getSupabaseAdmin();
    if (!supabaseAdmin) {
      console.warn('[Supabase Auth] extractUserIdFromSupabaseToken: supabaseAdmin not initialized');
      return null;
    }
    // Network fallback (symmetric/legacy tokens or local-verify miss).
    const { data: { user }, error } = await supabaseAdmin.auth.getUser(token);

    if (error || !user) {
      console.error('Error verifying token with Supabase:', error);

return null;
    }

    return user.id || null;
  } catch (error) {
    console.error('Error extracting user ID from Supabase token:', error);
    
return null;
  }
}

/**
 * Extract user type from Supabase JWT token in authorization header
 */
export async function extractUserTypeFromSupabaseToken(request: NextRequest): Promise<string | null> {
  try {
    const authHeader = request.headers.get('authorization');
    if (!authHeader || !authHeader.startsWith('Bearer ')) {
      return null;
    }

    const token = authHeader.substring(7); // Remove 'Bearer ' prefix
    
    const supabaseAdmin = getSupabaseAdmin();
    if (!supabaseAdmin) {
      console.warn('[Supabase Auth] extractUserTypeFromSupabaseToken: supabaseAdmin not initialized');
      return null;
    }
    // Use Supabase's built-in verification instead of manual JWT verification
    const { data: { user }, error } = await supabaseAdmin.auth.getUser(token);
    
    if (error || !user) {
      console.error('Error verifying token with Supabase:', error);
      
return null;
    }
    
    return user.user_metadata?.userType || 'user';
  } catch (error) {
    console.error('Error extracting user type from Supabase token:', error);
    
return null;
  }
}

/**
 * Validate Supabase authorization header and extract user information
 */
export function validateSupabaseAuthHeader(request: NextRequest): {
  isValid: boolean;
  email: string | null;
  userId: string | null;
  userType: string | null;
  token: string | null;
} {
  try {
    const authHeader = request.headers.get('authorization');
    if (!authHeader || !authHeader.startsWith('Bearer ')) {
      return {
        isValid: false,
        email: null,
        userId: null,
        userType: null,
        token: null,
      };
    }

    const token = authHeader.substring(7);
    const decoded = verifySupabaseJWT(token);

    if (!decoded) {
      return {
        isValid: false,
        email: null,
        userId: null,
        userType: null,
        token: null,
      };
    }

    return {
      isValid: true,
      email: decoded.email,
      userId: decoded.sub,
      userType: decoded.user_metadata?.userType || 'user',
      token,
    };
  } catch (error) {
    console.error('Error validating Supabase auth header:', error);
    
return {
      isValid: false,
      email: null,
      userId: null,
      userType: null,
      token: null,
    };
  }
}

// ============================================================================
// SUPABASE AUTHENTICATION FUNCTIONS
// ============================================================================

/**
 * Sign in with email and password using Supabase
 */
export async function signInWithSupabase(email: string, password: string) {
  try {
    const supabaseAuth = getSupabaseAuth();
    if (!supabaseAuth) {
      return { success: false, error: 'Supabase client not initialized' };
    }
    const { data, error } = await supabaseAuth.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      console.error('Supabase sign in error:', error);
      
return { success: false, error: error.message };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Error signing in with Supabase:', error);
    
return { success: false, error: 'An unexpected error occurred' };
  }
}

/**
 * Sign up with email and password using Supabase
 */
export async function signUpWithSupabase(email: string, password: string, userType: string = 'user') {
  try {
    const supabaseAuth = getSupabaseAuth();
    if (!supabaseAuth) {
      return { success: false, error: 'Supabase client not initialized' };
    }
    const { data, error } = await supabaseAuth.auth.signUp({
      email,
      password,
      options: {
        data: {
          userType,
        },
      },
    });

    if (error) {
      console.error('Supabase sign up error:', error);
      
return { success: false, error: error.message };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Error signing up with Supabase:', error);
    
return { success: false, error: 'An unexpected error occurred' };
  }
}

/**
 * Sign out using Supabase
 */
export async function signOutWithSupabase() {
  try {
    const supabaseAuth = getSupabaseAuth();
    if (!supabaseAuth) {
      return { success: false, error: 'Supabase client not initialized' };
    }
    // Device scope only — never global (see SignOutScope in @west-tennessee-consulting/supabase).
    const { error } = await supabaseAuth.auth.signOut({ scope: "local" });

    if (error) {
      console.error('Supabase sign out error:', error);
      
return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    console.error('Error signing out with Supabase:', error);
    
return { success: false, error: 'An unexpected error occurred' };
  }
}

/**
 * Get current user session from Supabase
 */
export async function getCurrentSupabaseUser() {
  try {
    const supabaseAuth = getSupabaseAuth();
    if (!supabaseAuth) {
      return null;
    }
    const { data: { user }, error } = await supabaseAuth.auth.getUser();

    if (error) {
      console.error('Error getting current Supabase user:', error);
      
return null;
    }

    return user;
  } catch (error) {
    console.error('Error getting current Supabase user:', error);
    
return null;
  }
}

/**
 * Get current session from Supabase
 */
export async function getCurrentSupabaseSession() {
  try {
    const supabaseAuth = getSupabaseAuth();
    if (!supabaseAuth) {
      return null;
    }
    const { data: { session }, error } = await supabaseAuth.auth.getSession();

    if (error) {
      console.error('Error getting current Supabase session:', error);
      
return null;
    }

    return session;
  } catch (error) {
    console.error('Error getting current Supabase session:', error);
    
return null;
  }
}

/**
 * Update user metadata in Supabase
 */
export async function updateSupabaseUserMetadata(userId: string, metadata: Record<string, any>) {
  try {
    const supabaseAdmin = getSupabaseAdmin();
    if (!supabaseAdmin) {
      return { success: false, error: 'Supabase admin client not initialized' };
    }
    const { data, error } = await supabaseAdmin.auth.admin.updateUserById(userId, {
      user_metadata: metadata,
    });

    if (error) {
      console.error('Error updating Supabase user metadata:', error);
      
return { success: false, error: error.message };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Error updating Supabase user metadata:', error);
    
return { success: false, error: 'An unexpected error occurred' };
  }
}

/**
 * Create user in Supabase (admin function)
 */
export async function createSupabaseUser(email: string, password: string, userType: string = 'user') {
  try {
    const supabaseAdmin = getSupabaseAdmin();
    if (!supabaseAdmin) {
      return { success: false, error: 'Supabase admin client not initialized' };
    }
    const { data, error } = await supabaseAdmin.auth.admin.createUser({
      email,
      password,
      user_metadata: {
        userType,
      },
      email_confirm: true, // Auto-confirm email
    });

    if (error) {
      console.error('Error creating Supabase user:', error);
      
return { success: false, error: error.message };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Error creating Supabase user:', error);
    
return { success: false, error: 'An unexpected error occurred' };
  }
}

/**
 * Delete user from Supabase (admin function)
 */
export async function deleteSupabaseUser(userId: string) {
  try {
    const supabaseAdmin = getSupabaseAdmin();
    if (!supabaseAdmin) {
      return { success: false, error: 'Supabase admin client not initialized' };
    }
    const { data, error } = await supabaseAdmin.auth.admin.deleteUser(userId);

    if (error) {
      console.error('Error deleting Supabase user:', error);
      
return { success: false, error: error.message };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Error deleting Supabase user:', error);
    
return { success: false, error: 'An unexpected error occurred' };
  }
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Get client IP address from request
 */
export function getClientIP(request: NextRequest): string | null {
  return request.headers.get('x-forwarded-for')?.split(',')[0] || 
         request.headers.get('x-real-ip') || 
         null;
}

/**
 * Get user agent from request
 */
export function getUserAgent(request: NextRequest): string | null {
  return request.headers.get('user-agent') || null;
}

/**
 * Check if user has admin role
 */
export function isAdminUser(userType: string | null): boolean {
  return userType === 'admin' || userType === 'super_admin';
}

/**
 * Check if user has manager role or higher
 */
export function isManagerOrHigher(userType: string | null): boolean {
  return ['admin', 'super_admin', 'manager'].includes(userType || '');
}

